'use client';

import { useEffect, useState } from 'react';
import useSWR from 'swr';
import { apiFetch } from '../lib/api';
import { useToast } from './Toast';
import { useTranslation } from '../lib/i18n';
import { useComposeModal } from './ComposeModal';

type EmailLink = {
  id: number;
  eventId: number;
  emailAccountId: number;
  emailUid: string;
  emailFolder: string | null;
  emailSubject: string | null;
  emailFrom: string | null;
  linkedByUserId: number | null;
  linkedAt: string | null;
};

type EmailSearchResult = {
  uid: string;
  accountId: number;
  subject: string | null;
  fromAddress: string | null;
  folder: string;
  receivedAt: string | null;
};

export default function EventEmailLinksSection({ eventId, contactId, accountPohodaId, eventTitle }: {
  eventId: number;
  contactId?: number | null;
  accountPohodaId?: string | null;
  eventTitle?: string | null;
}) {
  const { data: links, mutate } = useSWR<EmailLink[]>(
    `/api/user/calendar/events/${eventId}/emails`,
  );
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<EmailSearchResult[]>([]);
  const [linking, setLinking] = useState(false);
  const [resolvingRecipient, setResolvingRecipient] = useState(false);
  const { showToast } = useToast();
  const { t } = useTranslation();
  const { openCompose } = useComposeModal();

  const composeForEvent = async () => {
    setResolvingRecipient(true);
    try {
      let to = '';
      if (contactId) {
        const c = await apiFetch<{ email?: string | null }>(`/api/admin/obchod/contacts/${contactId}`, { silent: true }).catch(() => null);
        to = c?.email || '';
      }
      if (!to && accountPohodaId) {
        const a = await apiFetch<{ email?: string | null }>(`/api/user/obchod/accounts/${accountPohodaId}`, { silent: true }).catch(() => null);
        to = a?.email || '';
      }
      openCompose({ to, subject: eventTitle || undefined });
    } finally {
      setResolvingRecipient(false);
    }
  };

  useEffect(() => {
    if (!search.trim()) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(() => {
      apiFetch<{ content: EmailSearchResult[] }>(
        `/api/user/email/all/messages-live?search=${encodeURIComponent(search)}&size=20`
      ).then(data => setSearchResults(data?.content || [])).catch(() => setSearchResults([]));
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const handleLink = async (email: EmailSearchResult) => {
    setLinking(true);
    try {
      await apiFetch(`/api/user/calendar/events/${eventId}/emails`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          emailAccountId: email.accountId,
          emailUid: email.uid,
          emailFolder: email.folder,
          emailSubject: email.subject || '',
          emailFrom: email.fromAddress || '',
        }),
      });
      showToast('Email navázán na event', 'success');
      setSearch('');
      mutate();
    } catch {
      showToast('Chyba při navazování emailu', 'error');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async (linkId: number) => {
    try {
      await apiFetch(`/api/user/calendar/events/${eventId}/emails/${linkId}`, { method: 'DELETE' });
      mutate();
    } catch {
      showToast('Chyba při odstranění vazby', 'error');
    }
  };

  return (
    <div className="mt-4 p-4 bg-cream/50 border border-wheat rounded-rustic">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-bark">📧 Související emaily</h3>
        <button
          onClick={composeForEvent}
          disabled={resolvingRecipient}
          className="flex items-center gap-1 text-xs font-medium text-moss hover:text-moss-dark transition disabled:opacity-50"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
          {t('emailCompose')}
        </button>
      </div>

      {/* Linked emails */}
      {links && links.length > 0 && (
        <div className="space-y-1 mb-3 max-h-48 overflow-y-auto pr-1">
          {links.map(link => (
            <div key={link.id} className="flex items-center gap-2 text-sm bg-linen/70 border border-wheat/50 rounded px-2 py-1.5">
              <a
                href={`/email?account=${link.emailAccountId}&uid=${encodeURIComponent(link.emailUid)}&folder=${encodeURIComponent(link.emailFolder || 'INBOX')}`}
                className="flex-1 min-w-0 text-left hover:text-moss"
              >
                <span className="font-medium truncate block">{link.emailSubject || '(bez předmětu)'}</span>
                <span className="text-xs text-wood">{link.emailFrom}</span>
              </a>
              <button
                onClick={() => handleUnlink(link.id)}
                className="text-xs text-rust hover:text-rust-dark px-1.5 py-0.5 rounded hover:bg-rust/10 transition"
                title="Odstranit vazbu"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
      {links && links.length === 0 && (
        <p className="text-xs text-wood mb-3">Zatím žádné navázané emaily.</p>
      )}

      {/* Search */}
      <input
        type="text"
        placeholder={t('eventEmailLinksSearch')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full px-3 py-2 border border-wheat rounded text-sm bg-linen/50 text-bark focus:ring-1 focus:ring-moss outline-none"
      />
      {searchResults.length > 0 && (
        <div className="mt-1 border border-wheat rounded bg-linen max-h-48 overflow-y-auto text-sm">
          {searchResults.map(email => {
            const alreadyLinked = links?.some(l => l.emailAccountId === email.accountId && l.emailUid === email.uid);
            return (
              <button
                key={`${email.accountId}:${email.uid}`}
                onClick={() => handleLink(email)}
                disabled={linking || alreadyLinked}
                className="block w-full text-left px-2 py-1.5 hover:bg-wheat/30 transition text-bark disabled:opacity-40"
              >
                <span className="font-medium">{email.subject || '(bez předmětu)'}</span>
                <span className="text-xs text-wood block">{email.fromAddress}</span>
                {alreadyLinked && <span className="text-xs text-moss">✓ Již navázáno</span>}
              </button>
            );
          })}
        </div>
      )}
      {search.trim() && searchResults.length === 0 && (
        <p className="text-xs text-wood text-center mt-2">Žádné emaily nenalezeny</p>
      )}
    </div>
  );
}
